import { useInfiniteQuery } from '@tanstack/react-query';
import type { AgentApiCandidateProfilesIndex } from '@/types/serializers';
import { fetchJson } from '@/utils/apiFetch';

export type CustomFieldContent =
  string | number | number[] | { min?: string | number; max?: string | number };

export type CustomFieldQuery = {
  field_id: number;
  content: CustomFieldContent;
};

export type Request = {
  keyword?: string;
  name?: string;
  sort?: string;
  organization_user_ids?: number[];
  custom_tag_ids?: number[];
  custom_tag_match?: 'all' | 'any';
  candidate_board_ids?: number[];
  custom_fields?: CustomFieldQuery[];
  age?: {
    min?: number;
    max?: number;
  };
  include_finished?: boolean;
  page?: number;
};

const buildQueryParams = (query?: Request): string => {
  if (!query) return '';

  const params: string[] = [];

  if (query.keyword) {
    params.push(`search_query[keyword]=${encodeURIComponent(query.keyword)}`);
  }

  if (query.name) {
    params.push(`search_query[name]=${encodeURIComponent(query.name)}`);
  }

  if (query.sort) {
    params.push(`search_query[sort]=${encodeURIComponent(query.sort)}`);
  }

  if (query.organization_user_ids && query.organization_user_ids.length > 0) {
    query.organization_user_ids.forEach((id) => {
      params.push(`search_query[organization_user_ids][]=${id}`);
    });
  }

  if (query.custom_tag_ids && query.custom_tag_ids.length > 0) {
    query.custom_tag_ids.forEach((id) => {
      params.push(`search_query[custom_tag_ids][]=${id}`);
    });
  }

  if (query.custom_tag_match === 'any') {
    params.push(`search_query[custom_tag_match]=any`);
  }

  if (query.candidate_board_ids && query.candidate_board_ids.length > 0) {
    query.candidate_board_ids.forEach((id) => {
      params.push(`search_query[candidate_board_ids][]=${id}`);
    });
  }

  if (query.custom_fields && query.custom_fields.length > 0) {
    query.custom_fields.forEach((field) => {
      params.push(`search_query[custom_fields][][field_id]=${field.field_id}`);

      if (Array.isArray(field.content)) {
        field.content.forEach((value) => {
          params.push(
            `search_query[custom_fields][][content][]=${encodeURIComponent(String(value))}`
          );
        });
      } else if (typeof field.content === 'object' && field.content !== null) {
        if (field.content.min !== undefined) {
          params.push(`search_query[custom_fields][][content][min]=${field.content.min}`);
        }
        if (field.content.max !== undefined) {
          params.push(`search_query[custom_fields][][content][max]=${field.content.max}`);
        }
      } else {
        params.push(
          `search_query[custom_fields][][content]=${encodeURIComponent(String(field.content))}`
        );
      }
    });
  }

  if (query.age) {
    if (query.age.min !== undefined && query.age.min !== 0) {
      params.push(`search_query[age][min]=${query.age.min}`);
    }
    if (query.age.max !== undefined && query.age.max !== 0) {
      params.push(`search_query[age][max]=${query.age.max}`);
    }
  }

  if (query.include_finished) {
    params.push(`search_query[include_finished]=true`);
  }

  if (query.page !== undefined) {
    params.push(`page=${query.page}`);
  }

  return params.length > 0 ? `?${params.join('&')}` : '';
};

export default function useFetchCandidateProfiles(query?: Request, { enabled = true } = {}) {
  return useInfiniteQuery({
    queryKey: ['candidateProfiles', query],
    enabled,
    queryFn: async ({ pageParam = 1 }) => {
      const queryWithPage = query ? { ...query, page: pageParam } : { page: pageParam };
      const queryString = buildQueryParams(queryWithPage);
      const url = `/agent/api/candidate_profiles.json${queryString}`;
      const data = await fetchJson<AgentApiCandidateProfilesIndex>(url);
      return data;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.is_last_page) {
        return undefined;
      }
      return allPages.length + 1;
    },
    initialPageParam: 1,
  });
}
